import { useReduxColaborador } from "#colaborador/redux/slice"
import ButtonIcon from "#common/buttons/ButtonIcon"
import { MasterDialog } from "#common/Dialog/Dialog"
import { formatMoney } from "#common/Funciones"
import { InputSelect } from "#common/input/InputSelect"
import { LoadViewTable } from "#common/LoadingView"
import { DataItem } from "#common/text/DataItem"
import { RED_COLOR } from "#constants/Styles"
import { useApi } from "#hooks/useApi"
import TableInfoDialog from "#salaries/components/TableInfoDialog"
import {
	periodsColaboradoresGet,
	periodsGet,
	salariesPDFGet,
} from "#salaries/services/Api"
import FileDownloadIcon from "@mui/icons-material/FileDownload"
import InfoIcon from "@mui/icons-material/Info"
import { Box, Stack } from "@mui/material"
import { saveAs } from "file-saver"
import MUIDataTable from "mui-datatables"
import { useEffect, useMemo, useState } from "react"

const Table = ({ onClick }) => {
	const { reloadTable } = useReduxColaborador()
	const [selectPeriod, setSelectPeriod] = useState("")
	const params = useMemo(() => ({ period: selectPeriod }), [selectPeriod])
	const { data, loading, error } = useApi(periodsColaboradoresGet, {
		autoFetch: true,
		reload: reloadTable,
		params,
	})
	const colaboradores = useMemo(() => data?.colaboradores || [], [data?.colaboradores])

	const optionCellGeneral = {
		customBodyRender: renderCustomBody,
	}
	function renderCustomBody(value, { rowIndex }) {
		const { fecha_baja } = colaboradores[rowIndex]
		let color = fecha_baja === null ? "" : RED_COLOR
		return <span style={{ color: color }}>{value}</span>
	}

	const customBodyRender = (value, tableMeta) => {
		return formatMoney(value, colaboradores[tableMeta.rowIndex]?.currency)
	}
	const columns = [
		{
			name: "no_empleado",
			label: "No empleado",
			options: {
				filter: true,
				sort: true,
				...optionCellGeneral,
			},
		},
		{
			name: "full_name",
			label: "Nombre",
			options: {
				filter: false,
				sort: true,
			},
		},
		{
			name: "compensation",
			label: "Persepciones",
			options: {
				filter: false,
				sort: true,
				customBodyRender,
			},
		},
		{
			name: "deduction",
			label: "Deducciones",
			options: {
				filter: false,
				sort: true,
				customBodyRender,
			},
		},
		{
			name: "total",
			label: "Total Neto",
			options: {
				filter: false,
				sort: true,
				customBodyRender,
			},
		},
		{
			name: "observation",
			label: "Observación",
			options: {
				filter: false,
				sort: true,
			},
		},
		{
			name: "info",
			label: "",
			options: {
				display: true,
				filter: false,

				sort: false,
				customBodyRender: (value, { rowIndex }) => {
					return (
						<Stack direction="row" spacing={2}>
							<TableInfoDialog
								data={value}
								currency={colaboradores[rowIndex]?.currency}
							/>
							<ReceiptCard
								id={colaboradores[rowIndex]?.id}
								name={`${colaboradores[rowIndex]?.full_name}-${colaboradores[rowIndex]?.no_empleado}`}
							/>
						</Stack>
					)
				},
			},
		},
	]

	const onRowClick = (rowData, { dataIndex }) => {
		if (onClick && typeof onClick === "function") onClick(colaboradores[dataIndex])
	}

	const options = {
		selectableRowsHideCheckboxes: true,
		print: false,
		viewColumns: false,
		onRowClick,
	}
	let tabla = null
	if (loading || error) tabla = <LoadViewTable />
	else tabla = <MUIDataTable data={colaboradores} columns={columns} options={options} />
	return (
		<Box>
			<SelectPeriod {...{ selectPeriod, setSelectPeriod }} />
			{tabla}
		</Box>
	)
}

export default Table

const SelectPeriod = ({ selectPeriod, setSelectPeriod }) => {
	const { data } = useApi(periodsGet, { autoFetch: true })
	useEffect(() => {
		if (data?.periods) setSelectPeriod(data?.periods[0].id)
	}, [data?.periods, setSelectPeriod])
	const periods = data?.periods.map((period) => {
		return { id: period.id, name: `${period.name} (${period.start} - ${period.end})` }
	})
	return (
		<InputSelect
			label={"Periodo"}
			data={periods}
			dataId="id"
			dataLabel="name"
			setValue={setSelectPeriod}
			value={selectPeriod}
		/>
	)
}

const InfoDialog = ({ data, currency }) => {
	const [open, setOpen] = useState(false)
	const text = {
		numero_x: "INDEX TABLA EXCEL",
		nombre: "NOMBRE",
		sucursal: "SUCURSAL",
		perfil_puesto: "PERFIL PUESTO",
		cuenta: "CUENTA",
		bonos: "BONOS",
		extras: "EXTRAS",
		faltas: "FALTAS",
		destajo: "DESTAJO",
		hrs_extra: "HORAS EXTRA",
		antiguedad: "ANTIGÜEDAD",
		precio_hora: "PRECIO POR HORA",
		observaciones: "OBSERVACIONES",
		dias_laborados: "DIAS LABORADOS",
		salario_diario: "SALARIO DIARIO",
		salario_semanal: "SALARIO SEMANAL",
		vacaciones_prima: "VAC + PRIMA VACACIONAL",
		deducciones_faltas: "DEDUCCIONES FALTAS",
		deducciones_bancaria: "DEDUCCIONES BANCARIA",
		deducciones_prestamo: "DEDUCCIONES PRESTAMO",
		observaciones_extras: "OBSERVACIONES EXTRAS",
		deducciones_saldo_a_favor: "DEDUCCIONES SALDO A FAVOR",
		deducciones_creditos_infonavit_fonacot: "CRÉDITOS INFONAVIT / FONACOT",
		total_neto: "TOTAL NETO",
	}
	const no_money = [
		"numero_x",
		"nombre",
		"sucursal",
		"perfil_puesto",
		"cuenta",
		"extras",
		"faltas",
		"observaciones",
		"dias_laborados",
		"observaciones_extras",
	]
	const info = Object.keys(text).map((key) => {
		let value = data[key]
		if (value == null) value = "N/A"
		else {
			if (!no_money.includes(key)) value = formatMoney(value, currency)
		}

		return <DataItem key={key} label={text[key]} value={value} />
	})
	return (
		<>
			<ButtonIcon
				title="Información"
				icon={<InfoIcon />}
				onClick={() => setOpen(true)}
			/>
			<MasterDialog
				title={"Información subida del excel"}
				{...{
					open,
					setOpen,
				}}
			>
				{info}
			</MasterDialog>
		</>
	)
}

const ReceiptCard = ({ id, name }) => {
	const { fetch, error, data } = useApi(salariesPDFGet, { responseAll: true })
	useEffect(() => {
		if (!data) return
		saveAs(data, `${name}-recibo.pdf`)
	}, [data, name])

	return (
		<ButtonIcon
			label="Descargar"
			icon={<FileDownloadIcon />}
			onClick={() => {
				fetch(id)
			}}
		/>
	)
}
